import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { api, getWedding, updateWedding } from '../../../lib/api';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { LANGUAGES } from '../../../i18n';

const SUB_TABS = [
  { id: 'general', label: 'General' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'billing', label: 'Billing' },
];

// ─── WhatsApp Integration Card ───────────────────────────────────────────────
const WhatsAppCard = ({ token, weddingId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [connectedInfo, setConnectedInfo] = useState(null);
  const [error, setError] = useState(null);
  const qrInterval = useRef(null);
  const expectedSessionName = weddingId ? `wedding-${weddingId}` : 'default';

  const fetchSessions = useCallback(async () => {
    try {
      const data = await api.getWhatsappSessions(token);
      const sessionList = Array.isArray(data) ? data : [];
      setSessions(sessionList);

      // Find working session for THIS wedding only
      const workingSession = sessionList.find(s => s.status === 'WORKING' && s.name === expectedSessionName);
      if (workingSession) {
        try {
          const me = await api.getWhatsappMe(token, workingSession.name);
          setConnectedInfo({ ...me, sessionName: workingSession.name });
        } catch {
          setConnectedInfo(null);
        }
      } else {
        setConnectedInfo(null);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, expectedSessionName]);

  useEffect(() => {
    fetchSessions();
    return () => {
      if (qrInterval.current) clearInterval(qrInterval.current);
    };
  }, [fetchSessions]);

  const handleStartSession = async () => {
    setStarting(true);
    setError(null);
    try {
      const sessionName = weddingId ? `wedding-${weddingId}` : 'default';
      await api.startWhatsappSession(token, sessionName, weddingId);
      // Start polling for QR code
      setQrLoading(true);
      setTimeout(async () => {
        await pollQR();
      }, 2000);
    } catch (err) {
      setError(err.message);
      setStarting(false);
    }
  };

  const pollQR = async () => {
    const sessionName = weddingId ? `wedding-${weddingId}` : 'default';
    try {
      const data = await api.getWhatsappQR(token, sessionName);
      if (data?.value) {
        setQrData(data.value);
        setQrLoading(false);
        setStarting(false);
        // Keep polling to detect connection + refresh QR
        qrInterval.current = setInterval(async () => {
          try {
            // Check session status first
            const sessions = await api.getWhatsappSessions(token);
            const list = Array.isArray(sessions) ? sessions : [];
            const working = list.find(s => s.status === 'WORKING' && s.name === expectedSessionName);
            if (working) {
              // Connected! Stop polling and show connected state
              clearInterval(qrInterval.current);
              setQrData(null);
              setQrLoading(false);
              setStarting(false);
              setSessions(list);
              try {
                const me = await api.getWhatsappMe(token, working.name);
                setConnectedInfo({ ...me, sessionName: working.name });
              } catch {
                setConnectedInfo({ sessionName: working.name });
              }
              return;
            }
            // Still waiting — refresh QR
            const freshData = await api.getWhatsappQR(token, sessionName).catch(() => null);
            if (freshData?.value) {
              setQrData(freshData.value);
            }
          } catch {
            clearInterval(qrInterval.current);
            setQrData(null);
            await fetchSessions();
          }
        }, 3000);
      } else {
        // QR not ready yet, retry
        setTimeout(pollQR, 2000);
      }
    } catch {
      setQrLoading(false);
      setStarting(false);
      await fetchSessions();
    }
  };

  const handleDisconnect = async () => {
    try {
      const sessionName = connectedInfo?.sessionName || 'default';
      await api.logoutWhatsappSession(token, sessionName);
      setConnectedInfo(null);
      setQrData(null);
      if (qrInterval.current) clearInterval(qrInterval.current);
      await fetchSessions();
    } catch (err) {
      setError(err.message);
    }
  };

  const workingSession = sessions.find(s => s.status === 'WORKING' && s.name === expectedSessionName);
  const isConnected = workingSession && connectedInfo;

  return (
    <div className="p-6 rounded-2xl border border-border bg-card group hover:shadow-romantic-sm transition-shadow">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#25D366]/10 text-[#25D366] rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2M12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 15 3.8 13.47 3.8 11.91C3.81 7.37 7.5 3.67 12.05 3.67Z" />
              </svg>
            </div>
            <div>
              <h4 className="font-bold text-foreground flex items-center gap-2">
                WhatsApp
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/20">PREMIUM</span>
              </h4>
              <p className="text-xs text-muted-foreground">Link your device to send RSVPs & reminders.</p>
            </div>
          </div>

          {loading ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              Checking connection...
            </div>
          ) : isConnected ? (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg">
                <Icon name="CheckCircle" size={16} />
                Connected as {connectedInfo?.pushName || connectedInfo?.id?.user || 'Unknown'}
              </div>
              {connectedInfo?.id?.user && (
                <p className="text-sm text-muted-foreground">
                  Phone: +{connectedInfo.id.user}
                </p>
              )}
              <button
                onClick={handleDisconnect}
                className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1.5 transition-colors"
              >
                <Icon name="LogOut" size={14} />
                Disconnect Device
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-foreground leading-relaxed">
                Open WhatsApp on your phone, go to <strong>Settings &gt; Linked Devices</strong> and scan the QR code to connect.
              </p>
              {!qrData && !qrLoading && (
                <Button
                  onClick={handleStartSession}
                  disabled={starting}
                  className="bg-[#25D366] hover:bg-[#1da851] text-white"
                >
                  {starting ? 'Starting...' : 'Connect WhatsApp'}
                </Button>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-600 flex items-center gap-1.5 font-medium">
                <Icon name="AlertCircle" size={14} />
                {error.includes('auth failed') || error.includes('502')
                  ? 'WhatsApp service authentication failed'
                  : error}
              </p>
              {(error.includes('auth failed') || error.includes('502')) && (
                <p className="text-xs text-red-500 mt-1.5 ml-5">
                  The API key doesn't match the WhatsApp service. Please update it via{' '}
                  <code className="bg-red-100 px-1 rounded text-[11px]">wrangler secret put ANYWAZ_API_KEY</code>
                </p>
              )}
            </div>
          )}
        </div>

        {/* QR Code area */}
        {(qrData || qrLoading) && !isConnected && (
          <div className="shrink-0 flex flex-col items-center justify-center p-6 bg-white border border-border rounded-xl shadow-inner-soft w-full md:w-auto">
            {qrLoading ? (
              <div className="w-64 h-64 flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-[#25D366] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : qrData ? (
              <img
                src={qrData.startsWith('data:') ? qrData : `data:image/png;base64,${qrData}`}
                alt="WhatsApp QR Code"
                className="w-64 h-64"
                style={{ imageRendering: 'pixelated' }}
              />
            ) : null}
            <span className="text-xs font-medium text-muted-foreground mt-3">Scan with WhatsApp to link</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Instagram Integration Card ──────────────────────────────────────────────
const InstagramCard = ({ token, content, onUpdateContent, weddingId }) => {
  const [hashtag1, setHashtag1] = useState('');
  const [hashtag2, setHashtag2] = useState('');
  const [igUser, setIgUser] = useState(null);
  const [igLoading, setIgLoading] = useState(true);
  const [igConnecting, setIgConnecting] = useState(false);
  const [igError, setIgError] = useState(null);

  useEffect(() => {
    const ig = content?.instagram || {};
    setHashtag1(ig.hashtag1 || '');
    setHashtag2(ig.hashtag2 || '');
  }, [content]);

  useEffect(() => {
    if (!token) return;
    api.getInstagramUser(token, weddingId)
      .then((data) => {
        setIgUser(data?.connected ? data : null);
        if (data?.hashtag1 && !hashtag1) setHashtag1(data.hashtag1);
        if (data?.hashtag2 && !hashtag2) setHashtag2(data.hashtag2);
      })
      .catch(() => setIgUser(null))
      .finally(() => setIgLoading(false));
  }, [token]);

  const handleSave = () => {
    onUpdateContent({
      ...content,
      instagram: { ...(content?.instagram || {}), hashtag1, hashtag2 },
    });
  };

  const handleLogin = async () => {
    setIgConnecting(true);
    setIgError(null);
    try {
      const { authUrl } = await api.getInstagramAuthUrl(token, weddingId);
      window.location.href = authUrl;
    } catch (err) {
      setIgError(err.message);
      setIgConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.disconnectInstagram(token, weddingId);
      setIgUser(null);
    } catch (err) {
      setIgError(err.message);
    }
  };

  return (
    <div className="p-6 rounded-2xl border border-border bg-card relative overflow-hidden group hover:shadow-romantic-sm transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
            </svg>
          </div>
          <div>
            <h4 className="font-bold text-foreground">Instagram</h4>
            <p className="text-xs text-muted-foreground">
              {igUser ? `Connected as @${igUser.username}` : 'Connect account & hashtags'}
            </p>
          </div>
        </div>
        {igLoading ? (
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        ) : igUser ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg flex items-center gap-1">
              <Icon name="CheckCircle" size={12} /> Connected
            </span>
            <button
              onClick={handleDisconnect}
              className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors"
            >
              <Icon name="LogOut" size={12} /> Disconnect
            </button>
          </div>
        ) : (
          <button
            onClick={handleLogin}
            disabled={igConnecting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E1306C] text-white text-sm font-bold shadow-sm hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            <Icon name="LogIn" size={14} />
            {igConnecting ? 'Connecting...' : 'Login with Instagram'}
          </button>
        )}
      </div>
      {igError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-600 flex items-center gap-1.5">
            <Icon name="AlertCircle" size={14} />
            {igError.includes('not configured')
              ? 'Instagram not configured. Add INSTAGRAM_APP_ID and INSTAGRAM_APP_SECRET to your worker secrets.'
              : igError}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary font-bold">#</span>
          <input
            className="w-full bg-background border border-border rounded-lg py-2 pl-7 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            placeholder="FirstHashtag"
            type="text"
            value={hashtag1}
            onChange={(e) => setHashtag1(e.target.value)}
            onBlur={handleSave}
          />
        </div>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary font-bold">#</span>
          <input
            className="w-full bg-background border border-border rounded-lg py-2 pl-7 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            placeholder="SecondHashtag"
            type="text"
            value={hashtag2}
            onChange={(e) => setHashtag2(e.target.value)}
            onBlur={handleSave}
          />
        </div>
      </div>
    </div>
  );
};

// ─── YouTube & Google Drive Cards ────────────────────────────────────────────
const LinkCard = ({ icon, iconBg, iconColor, title, badge, badgeColor, description, placeholder, value, onChange }) => (
  <div className="p-6 rounded-2xl border border-border bg-card group hover:shadow-romantic-sm transition-shadow">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 ${iconBg} ${iconColor} rounded-lg flex items-center justify-center`}>
        <Icon name={icon} size={20} />
      </div>
      <div>
        <h4 className="font-bold text-foreground flex items-center gap-2">
          {title}
          {badge && (
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${badgeColor}`}>{badge}</span>
          )}
        </h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
    <input
      className="w-full bg-background border border-border rounded-lg py-2 px-3 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
      placeholder={placeholder}
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// ─── General Section ─────────────────────────────────────────────────────────
const GeneralSection = ({ content, onUpdateContent, saving, token, weddingId, subscription }) => {
  const [hashtag, setHashtag] = useState('');
  const [location, setLocation] = useState('');
  const [domain, setDomain] = useState('');
  const [language, setLanguage] = useState('en');
  const [domainCheckResult, setDomainCheckResult] = useState(null);
  const [domainChecking, setDomainChecking] = useState(false);
  const [domainRegistering, setDomainRegistering] = useState(false);
  const [domainRegistered, setDomainRegistered] = useState(false);
  const [domainError, setDomainError] = useState(null);

  useEffect(() => {
    setHashtag(content?.settings?.hashtag || '');
    setLocation(content?.settings?.eventLocationUrl || '');
    setDomain(content?.settings?.customDomain || '');
    setLanguage(content?.settings?.language || 'en');
  }, [content]);

  const handleSave = () => {
    onUpdateContent({
      ...content,
      settings: {
        ...(content?.settings || {}),
        hashtag,
        eventLocationUrl: location,
        customDomain: domain,
        language,
      },
    });
  };

  const handleCheckAvailability = async () => {
    if (!domain.trim()) {
      setDomainError('Please enter a domain name');
      return;
    }
    setDomainChecking(true);
    setDomainError(null);
    setDomainCheckResult(null);
    try {
      const result = await api.checkDomainAvailability(token, domain.trim());
      setDomainCheckResult(result);
    } catch (err) {
      setDomainError(err.message);
    } finally {
      setDomainChecking(false);
    }
  };

  const handleRegisterDomain = async () => {
    if (!weddingId) {
      setDomainError('Wedding ID is required');
      return;
    }
    setDomainRegistering(true);
    setDomainError(null);
    try {
      await api.registerDomain(token, domain.trim(), weddingId);
      setDomainRegistered(true);
      setDomainCheckResult(null);
      setTimeout(() => setDomainRegistered(false), 5000);
    } catch (err) {
      setDomainError(err.message);
    } finally {
      setDomainRegistering(false);
    }
  };

  const isPremiumOrAdvanced = subscription && ['premium', 'advanced', 'partner'].includes(subscription.planId?.toLowerCase());

  return (
    <div className="space-y-8">
      <section className="bg-card rounded-2xl shadow-romantic-sm p-6 md:p-8 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Edit3" size={22} className="text-primary" />
          <h3 className="text-xl font-bold text-foreground">Basic Details</h3>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-foreground">Wedding Hashtag</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-primary font-bold text-lg">#</span>
              <input
                className="w-full bg-background border border-border rounded-xl py-3 pl-10 pr-4 text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                placeholder="YourHashtag"
                type="text"
                value={hashtag}
                onChange={(e) => setHashtag(e.target.value)}
                onBlur={handleSave}
              />
            </div>
            <p className="text-xs text-muted-foreground">Primary hashtag for searching.</p>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Globe2" size={16} className="text-primary" />
              Guest Language
            </label>
            <select
              className="w-full bg-background border border-border rounded-xl py-3 px-4 text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value);
                onUpdateContent({
                  ...content,
                  settings: { ...(content?.settings || {}), language: e.target.value },
                });
              }}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">Language for your guest-facing invitation pages (RSVP, schedule, gallery, etc.)</p>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-foreground flex items-center gap-2">
              Custom Domain
              <span className="bg-primary/10 text-primary text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border border-primary/20">Premium/Advanced</span>
            </label>
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <div className="relative flex-1">
                <Icon name="Globe" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  className="w-full bg-background border border-border rounded-xl py-3 pl-12 pr-4 text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="www.yourdomain.com"
                  type="text"
                  value={domain}
                  onChange={(e) => {
                    setDomain(e.target.value);
                    setDomainCheckResult(null);
                    setDomainError(null);
                  }}
                  onBlur={handleSave}
                />
              </div>
              <Button
                onClick={handleCheckAvailability}
                disabled={domainChecking || !domain.trim()}
                className="whitespace-nowrap"
              >
                {domainChecking ? 'Checking...' : 'Check Availability'}
              </Button>
            </div>

            {/* Domain check result */}
            {domainCheckResult && (
              <div className={`p-3 rounded-lg border ${
                domainCheckResult.available
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon
                    name={domainCheckResult.available ? 'CheckCircle' : 'XCircle'}
                    size={16}
                    className={domainCheckResult.available ? 'text-emerald-600' : 'text-red-600'}
                  />
                  <span className={domainCheckResult.available ? 'text-emerald-600' : 'text-red-600'}>
                    {domainCheckResult.available ? 'Domain Available!' : 'Domain Not Available'}
                  </span>
                </div>
                {domainCheckResult.available && domainCheckResult.price && (
                  <p className="text-xs text-emerald-600 mt-1 ml-6">
                    Price: {domainCheckResult.currency} {domainCheckResult.price}
                    {domainCheckResult.premium && ' (Premium Domain)'}
                  </p>
                )}
              </div>
            )}

            {/* Register button for available domains */}
            {domainCheckResult?.available && (
              <div className="mt-2">
                {isPremiumOrAdvanced ? (
                  <Button
                    onClick={handleRegisterDomain}
                    disabled={domainRegistering}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {domainRegistering ? 'Registering...' : 'Register Domain'}
                  </Button>
                ) : (
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <p className="text-sm text-yellow-700 flex items-center gap-2">
                      <Icon name="Lock" size={14} />
                      Upgrade to Premium or Advanced plan to register domains
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Success message */}
            {domainRegistered && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-sm text-emerald-600 flex items-center gap-2">
                  <Icon name="CheckCircle" size={14} />
                  Domain registered successfully!
                </p>
              </div>
            )}

            {/* Error message */}
            {domainError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <Icon name="AlertCircle" size={14} />
                  {domainError}
                </p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">Enter your desired domain name to check if it's available for registration.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

// ─── Integrations Section ────────────────────────────────────────────────────
const IntegrationsSection = ({ token, content, onUpdateContent, weddingId }) => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [galleryUrl, setGalleryUrl] = useState('');

  useEffect(() => {
    setYoutubeUrl(content?.settings?.youtubeUrl || '');
    setGalleryUrl(content?.gallery?.publicGalleryUrl || '');
  }, [content]);

  const handleSettingSave = (field, value) => {
    onUpdateContent({
      ...content,
      settings: {
        ...(content?.settings || {}),
        [field]: value,
      },
    });
  };

  const handleGallerySave = (field, value) => {
    onUpdateContent({
      ...content,
      gallery: {
        ...(content?.gallery || {}),
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-8">
      <section className="bg-card rounded-2xl shadow-romantic-sm p-6 md:p-8 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Plug" size={22} className="text-primary" />
          <h3 className="text-xl font-bold text-foreground">Integrations</h3>
        </div>
        <div className="space-y-6">
          {/* Instagram */}
          <InstagramCard token={token} content={content} onUpdateContent={onUpdateContent} weddingId={weddingId} />

          {/* WhatsApp */}
          <WhatsAppCard token={token} weddingId={weddingId} />

          {/* YouTube & Google Drive */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <LinkCard
              icon="Play"
              iconBg="bg-red-600/10"
              iconColor="text-red-600"
              title="YouTube"
              badge="PRO+"
              badgeColor="bg-primary/10 text-primary border-primary/20"
              description="Wedding channel link"
              placeholder="https://youtube.com/c/..."
              value={youtubeUrl}
              onChange={(val) => {
                setYoutubeUrl(val);
                handleSettingSave('youtubeUrl', val);
              }}
            />
            <LinkCard
              icon="Camera"
              iconBg="bg-green-600/10"
              iconColor="text-green-600"
              title="Guest Gallery"
              badge="PRO+"
              badgeColor="bg-primary/10 text-primary border-primary/20"
              description="Google Photos / Drive link"
              placeholder="https://photos.google.com/..."
              value={galleryUrl}
              onChange={(val) => {
                setGalleryUrl(val);
                handleGallerySave('publicGalleryUrl', val);
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

// ─── Billing / Pricing Section ───────────────────────────────────────────────

// Currency detection based on wedding location
const detectCurrency = (content) => {
  const ceremonyAddress = content?.event?.ceremony?.address || '';
  const receptionAddress = content?.event?.reception?.address || '';
  // Also check schedule items' address and location fields
  const scheduleAddresses = (content?.schedule?.items || [])
    .map(item => `${item.address || ''} ${item.location || ''}`)
    .join(' ');
  const fullAddress = `${ceremonyAddress} ${receptionAddress} ${scheduleAddresses}`.toLowerCase();

  // Indonesian cities and keywords
  const indonesianKeywords = [
    'indonesia', 'jakarta', 'surabaya', 'bandung', 'bali', 'yogyakarta',
    'medan', 'semarang', 'makassar', 'palembang', 'tangerang', 'depok',
    'bekasi', 'bogor', 'batam', 'pekanbaru', 'bandar lampung', 'malang'
  ];

  // Malaysian cities and keywords
  const malaysianKeywords = [
    'malaysia', 'kuala lumpur', 'penang', 'johor', 'selangor', 'malacca',
    'melaka', 'ipoh', 'kota kinabalu', 'kuching', 'shah alam', 'petaling jaya'
  ];

  if (indonesianKeywords.some(keyword => fullAddress.includes(keyword))) {
    return 'IDR';
  }
  if (malaysianKeywords.some(keyword => fullAddress.includes(keyword))) {
    return 'MYR';
  }
  return 'USD';
};

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Start your journey',
    price: { IDR: 0, MYR: 0, USD: 0 },
    features: ['Basic Story Template', 'Up to 200 Guests'],
    storage: { monthly: '50 MB', '6month': '50 MB', yearly: '50 MB' },
    highlight: false,
    isFree: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For intimate weddings',
    price: { IDR: 499000, MYR: 149, USD: 49 },
    features: ['Pro Templates (Floral/Sage)', 'Up to 500 Guests', 'WhatsApp Integration', 'Background Audio'],
    storage: { monthly: '150 MB', '6month': '2 GB', yearly: '5 GB' },
    highlight: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'Everything in Pro, plus:',
    price: { IDR: 999000, MYR: 299, USD: 99 },
    features: ['All from Pro', 'Custom Domain', 'Up to 1,000 Guests', 'Instagram Integration'],
    storage: { monthly: '1 GB', '6month': '5 GB', yearly: '10 GB' },
    highlight: true,
    popular: true,
  },
  {
    id: 'advanced',
    name: 'Advanced',
    tagline: 'For grand celebrations',
    price: { IDR: 1999000, MYR: 599, USD: 199 },
    features: ['All from Premium', 'RSVP Reminder', 'On-site Check-in Guest System with Unique QR Code'],
    storage: { monthly: '2 GB', '6month': '10 GB', yearly: '20 GB' },
    highlight: false,
  },
];

// Calculate display price based on billing cycle
function getDisplayPrice(baseMonthlyPrice, billingCycle) {
  switch (billingCycle) {
    case '6month':
      return { total: Math.round(baseMonthlyPrice * 6 * 0.75 * 100) / 100, discount: 25 };
    case 'yearly':
      return { total: Math.round(baseMonthlyPrice * 12 * 0.50 * 100) / 100, discount: 50 };
    default:
      return { total: baseMonthlyPrice, discount: 0 };
  }
}

const BILLING_PERIODS = [
  { id: 'monthly', label: 'Monthly', suffix: '/mo' },
  { id: '6month', label: '6 Months', suffix: '/6mo', badge: '-25%' },
  { id: 'yearly', label: 'Yearly', suffix: '/yr', badge: '-50%' },
];

const BillingSection = ({ token, weddingId, content }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Auto-detect currency from wedding location
  const currency = detectCurrency(content);
  const currencySymbols = { IDR: 'Rp ', MYR: 'RM ', USD: '$' };
  const sym = currencySymbols[currency] || '$';

  const formatAmount = (amount) => currency === 'IDR'
    ? new Intl.NumberFormat('id-ID').format(amount)
    : amount.toLocaleString();

  useEffect(() => {
    if (!token) return;
    api.getSubscriptionStatus(token, weddingId)
      .then((data) => setSubscription(data?.subscription || null))
      .catch(() => setSubscription(null))
      .finally(() => setSubLoading(false));
  }, [token, weddingId]);

  // Handle PayPal return URL params — show confirmation modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('paypal') === 'success') {
      setShowConfirmation(true);
      if (token) {
        api.getSubscriptionStatus(token, weddingId)
          .then((data) => setSubscription(data?.subscription || null))
          .catch(() => {});
      }
    }
  }, [token]);

  const dismissConfirmation = () => {
    setShowConfirmation(false);
    const url = new URL(window.location);
    ['paypal', 'subscription_id', 'ba_token', 'token'].forEach(p => url.searchParams.delete(p));
    window.history.replaceState({}, '', url);
  };

  const handleSelectPlan = async (planId) => {
    setCheckoutLoading(planId);
    setError(null);
    try {
      const data = await api.createPayPalSubscription(planId, billingCycle, token, weddingId, currency);
      if (data?.approvalUrl) {
        window.location.href = data.approvalUrl;
      } else {
        setError('No approval URL returned from PayPal');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.paypalSubscriptionId) return;
    setCancelling(true);
    setError(null);
    try {
      await api.cancelPayPalSubscription(subscription.paypalSubscriptionId, token);
      setSubscription({ ...subscription, status: 'CANCELLED', cancelledAt: new Date().toISOString() });
      setCancelConfirm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const isActiveSub = subscription && ['ACTIVE', 'APPROVAL_PENDING'].includes(subscription.status);
  const currentPlanId = subscription?.planId;
  const isPartnerPlan = currentPlanId === 'partner';

  const getPlanButtonLabel = (plan) => {
    if (checkoutLoading === plan.id) return 'Redirecting...';
    if (currentPlanId === plan.id && isActiveSub) return subscription.status === 'ACTIVE' ? 'Current Plan' : 'Pending...';
    if (isActiveSub) return 'Switch Plan';
    return 'Select Plan';
  };

  return (
    <div className="space-y-8">
      {/* Payment Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95">
            <div className="bg-gradient-to-r from-primary to-primary/80 p-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Payment Successful!</h2>
              <p className="text-white/80 mt-2">Your subscription is now active</p>
            </div>
            <div className="p-6">
              {subscription && (
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="font-bold text-foreground">{PLANS.find(p => p.id === subscription.planId)?.name || subscription.planId}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Billing</span>
                    <span className="font-bold text-foreground">{subscription.billingCycle === '6month' ? '6 Months' : subscription.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                  </div>
                  {subscription.amount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-bold text-foreground">{sym}{formatAmount(subscription.amount)}</span>
                    </div>
                  )}
                  {subscription.nextBillingDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Next billing</span>
                      <span className="font-bold text-foreground">{new Date(subscription.nextBillingDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={dismissConfirmation}
                className="w-full py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors"
              >
                Go to Billing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Subscription Banner */}
      {subscription && (
        <section className="bg-card rounded-2xl shadow-romantic-sm p-6 border border-border">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CreditCard" size={18} className="text-primary" />
                <h3 className="text-lg font-bold text-foreground">Your Subscription</h3>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  subscription.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    : subscription.status === 'CANCELLED' ? 'bg-red-100 text-red-700 border border-red-200'
                    : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                }`}>
                  {subscription.status}
                </span>
              </div>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>Plan: <strong className="text-foreground">{PLANS.find(p => p.id === subscription.planId)?.name || subscription.planId}</strong> ({subscription.billingCycle === '6month' ? '6 Months' : subscription.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'})</p>
                {subscription.amount && <p>Amount: <strong className="text-foreground">{sym}{formatAmount(subscription.amount)}</strong></p>}
                {subscription.subscriberEmail && <p>PayPal: {subscription.subscriberEmail}</p>}
                {subscription.nextBillingDate && subscription.status === 'ACTIVE' && (
                  <p>Next billing: {new Date(subscription.nextBillingDate).toLocaleDateString()}</p>
                )}
                {subscription.cancelledAt && (
                  <p>Cancelled: {new Date(subscription.cancelledAt).toLocaleDateString()}</p>
                )}
              </div>
            </div>
            {subscription.status === 'ACTIVE' && (
              <div>
                {cancelConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-600 font-medium">Cancel subscription?</span>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg font-bold hover:bg-red-600 transition-colors disabled:opacity-60"
                    >
                      {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                    </button>
                    <button
                      onClick={() => setCancelConfirm(false)}
                      className="px-3 py-1.5 border border-border text-sm rounded-lg font-medium hover:bg-muted transition-colors"
                    >
                      No, Keep
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setCancelConfirm(true)}
                    className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1.5 transition-colors"
                  >
                    <Icon name="XCircle" size={14} />
                    Unsubscribe
                  </button>
                )}
              </div>
            )}
            {subscription.status === 'CANCELLED' && (
              <p className="text-sm text-muted-foreground italic">You can subscribe again by selecting a plan below.</p>
            )}
          </div>
        </section>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-600 flex items-center gap-1.5">
            <Icon name="AlertCircle" size={14} />
            {error}
          </p>
        </div>
      )}

      <section className="bg-card rounded-2xl shadow-romantic-sm p-6 md:p-8 border border-border">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Icon name="CreditCard" size={22} className="text-primary" />
            <h3 className="text-xl font-bold text-foreground">Plan & Pricing</h3>
          </div>
          {!isPartnerPlan && (
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground">
                Currency: <span className="font-bold text-foreground">{currency}</span>
              </div>
            </div>
          )}
        </div>

        {isPartnerPlan ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={28} className="text-emerald-600" />
            </div>
            <h4 className="text-xl font-bold text-emerald-800 mb-2">Partner Plan</h4>
            <p className="text-emerald-700 text-sm max-w-md mx-auto">
              You have an active Partner subscription with unlimited weddings, unlimited guests, and maximum storage.
              This plan is managed by OurDay administration.
            </p>
          </div>
        ) : (
        <>
        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-muted rounded-xl p-1 gap-1">
            {BILLING_PERIODS.map((period) => (
              <button
                key={period.id}
                onClick={() => setBillingCycle(period.id)}
                className={`relative px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  billingCycle === period.id
                    ? 'bg-primary text-white shadow-md'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {period.label}
                {period.badge && (
                  <span className={`absolute -top-2 -right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    billingCycle === period.id ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {period.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {PLANS.map((plan) => {
            const isCurrent = currentPlanId === plan.id && isActiveSub;
            const basePrice = plan.price[currency];
            const { total, discount } = plan.isFree
              ? { total: 0, discount: 0 }
              : getDisplayPrice(basePrice, billingCycle);
            const periodSuffix = BILLING_PERIODS.find(p => p.id === billingCycle)?.suffix || '/mo';
            const originalTotal = billingCycle === '6month'
              ? basePrice * 6
              : billingCycle === 'yearly'
              ? basePrice * 12
              : basePrice;

            return (
              <div
                key={plan.id}
                className={`rounded-2xl p-6 flex flex-col transition-colors ${
                  isCurrent
                    ? 'border-2 border-primary bg-primary/5 relative md:-translate-y-2 md:shadow-romantic-lg'
                    : plan.highlight && !isActiveSub
                    ? 'border-2 border-primary bg-primary/5 relative md:-translate-y-2 md:shadow-romantic-lg'
                    : 'border border-border bg-card hover:border-primary/50'
                }`}
              >
                {((plan.popular && !isActiveSub) || isCurrent) && (
                  <div className="absolute top-0 right-0 left-0 bg-primary text-white text-[10px] font-bold text-center py-1 rounded-t-lg mx-6 -mt-3 uppercase tracking-wider">
                    {isCurrent ? 'Your Plan' : 'Most Popular'}
                  </div>
                )}
                <div className={`mb-4 ${((plan.popular && !isActiveSub) || isCurrent) ? 'mt-2' : ''}`}>
                  <h4 className="text-lg font-bold text-foreground">{plan.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{plan.tagline}</p>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-3xl font-bold ${isCurrent || (plan.highlight && !isActiveSub) ? 'text-primary' : 'text-foreground'}`}>
                      {sym}{formatAmount(total)}
                    </span>
                    {!plan.isFree && <span className="text-sm text-muted-foreground">{periodSuffix}</span>}
                  </div>
                  {!plan.isFree && discount > 0 && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground line-through">{sym}{formatAmount(originalTotal)}</span>
                      <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Save {discount}%</span>
                    </div>
                  )}
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-xs text-foreground">
                      <Icon
                        name={isCurrent || (plan.highlight && !isActiveSub) ? 'CheckCircle' : 'Check'}
                        size={16}
                        className={isCurrent || (plan.highlight && !isActiveSub) ? 'text-primary' : 'text-emerald-500'}
                      />
                      {feature}
                    </li>
                  ))}
                  <li className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <Icon name="HardDrive" size={16} className="text-blue-500" />
                    {plan.storage[billingCycle] || plan.storage.monthly} Storage
                  </li>
                </ul>
                {!plan.isFree && (
                  <button
                    onClick={() => !isCurrent && handleSelectPlan(plan.id)}
                    disabled={isCurrent || checkoutLoading === plan.id}
                    className={`w-full py-2.5 rounded-xl font-bold transition-all ${
                      isCurrent
                        ? 'bg-primary/20 text-primary cursor-default'
                        : checkoutLoading === plan.id
                        ? 'bg-primary/60 text-white cursor-wait'
                        : 'bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/30'
                    }`}
                  >
                    {getPlanButtonLabel(plan)}
                  </button>
                )}
                {plan.isFree && (
                  <div className="w-full py-2.5 rounded-xl font-bold text-center bg-muted text-muted-foreground">
                    Free Forever
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-border pt-8 flex flex-col items-center">
          <p className="text-sm text-muted-foreground mb-3">Secure payment processing via PayPal</p>
          <div className="flex gap-4 opacity-50">
            <span className="font-bold text-xs text-muted-foreground">VISA</span>
            <span className="font-bold text-xs text-muted-foreground">MASTERCARD</span>
            <span className="font-bold text-xs text-muted-foreground">AMEX</span>
            <span className="font-bold text-xs text-muted-foreground">PAYPAL</span>
          </div>
        </div>
        </>
        )}
      </section>
    </div>
  );
};

// ─── Main Settings Tab ───────────────────────────────────────────────────────
const SettingsTab = ({ weddingId }) => {
  const { token } = useAdminAuth();
  const [activeSubTab, setActiveSubTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('paypal') ? 'billing' : 'general';
  });
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      if (weddingId && token) {
        const data = await getWedding(token, weddingId);
        const c = typeof data.wedding.content === 'string'
          ? JSON.parse(data.wedding.content)
          : data.wedding.content;
        setContent(c);
      } else {
        const data = await api.getContent();
        setContent(data);
      }
    } catch (err) {
      console.error('Failed to load content:', err);
    } finally {
      setLoading(false);
    }
  }, [weddingId, token]);

  const fetchSubscription = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.getSubscriptionStatus(token, weddingId);
      setSubscription(data?.subscription || null);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setSubscription(null);
    }
  }, [token, weddingId]);

  useEffect(() => {
    fetchContent();
    fetchSubscription();
  }, [fetchContent, fetchSubscription]);

  const handleUpdateContent = async (updated) => {
    setContent(updated);
    setSaving(true);
    setSaveStatus(null);
    try {
      if (weddingId) {
        await updateWedding(token, weddingId, updated);
      } else {
        await api.updateContent(updated, token);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      setSaveStatus('error');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Wedding Settings</h2>
          <p className="text-muted-foreground mt-1">Manage your invitation details, integrations, and billing.</p>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus === 'saved' && (
            <span className="text-sm text-emerald-500 flex items-center gap-1">
              <Icon name="Check" size={14} /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-red-500 flex items-center gap-1">
              <Icon name="AlertCircle" size={14} /> Failed to save
            </span>
          )}
          <Button variant="outline" onClick={() => window.open('/', '_blank')}>
            Preview
          </Button>
          <Button onClick={() => handleUpdateContent(content)} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex overflow-x-auto gap-8 border-b border-border scrollbar-hide">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`pb-3 px-1 font-semibold text-sm border-b-[3px] transition-all whitespace-nowrap ${
              activeSubTab === tab.id
                ? 'text-primary border-primary'
                : 'text-muted-foreground hover:text-foreground border-transparent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeSubTab === 'general' && (
        <GeneralSection
          content={content}
          onUpdateContent={handleUpdateContent}
          saving={saving}
          token={token}
          weddingId={weddingId}
          subscription={subscription}
        />
      )}
      {activeSubTab === 'integrations' && (
        <IntegrationsSection token={token} content={content} onUpdateContent={handleUpdateContent} weddingId={weddingId} />
      )}
      {activeSubTab === 'billing' && (
        <BillingSection token={token} weddingId={weddingId} content={content} />
      )}
    </div>
  );
};

export default SettingsTab;
